import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Checkbox,
  CircularProgress,
  TableSortLabel,
  TablePagination
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useBillingInvoicing } from '../context/BillingInvoicingContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getBillingItemsByOrder,
  PaginatedResponse
} from '../../../services/billingItemService';
import { BillingItem } from '../../../models/billingItem';
import { formatBillingItemDescription } from '../utils/descriptionFormatter';
import SuspenseLoader from 'src/components/SuspenseLoader';

const BillingItemSummary: React.FC = () => {
  const { t } = useTranslation();
  const {
    selectedOrders,
    setCurrentStep,
    approveBillingItem,
    approveBillingItemsBulk,
    rejectBillingItem,
    billingItemActionLoading,
    billingItemActionError,
    reviewBillingItem,
    reviewBillingItemsBulk,
    reviewBillingItemLoading,
    reviewBillingItemError,
    currentStep,
    setBillingItems
  } = useBillingInvoicing();
  const { user } = useAuth();
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject' | 'review' | null;
    itemId: string | number | null;
  }>({ open: false, action: null, itemId: null });
  const [comment, setComment] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sort, setSort] = useState<string>('id,asc');
  const [loading, setLoading] = useState(false);
  const [billingItems, setBillingItemsState] = useState<BillingItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);
  const [loadingBulkAction, setLoadingBulkAction] = useState(false);

  // Fetch billing items for the first selected order (for simplicity)
  const order = selectedOrders[0];

  // Ensure order.id is a number
  const orderIdNum = order ? Number(order.id) : undefined;

  useEffect(() => {
    if (!orderIdNum) return;
    setLoading(true);
    getBillingItemsByOrder(orderIdNum, page, rowsPerPage, sort)
      .then((res: PaginatedResponse<BillingItem>) => {
        setBillingItemsState(Array.isArray(res.content) ? res.content : []);
        setTotalItems(
          typeof res.totalElements === 'number' ? res.totalElements : 0
        );
        setSelected([]);
      })
      .catch(() => {
        setBillingItemsState([]);
        setTotalItems(0);
        setSelected([]);
      })
      .finally(() => setLoading(false));
  }, [orderIdNum, page, rowsPerPage, sort]);

  // Sorting
  const handleSort = (property: string) => {
    const [currentProp, currentDir] = sort.split(',');
    let direction = 'asc';
    if (currentProp === property && currentDir === 'asc') direction = 'desc';
    setSort(`${property},${direction}`);
  };

  // Frontend sorting logic
  const getSortedItems = (items: BillingItem[]) => {
    const [property, direction] = sort.split(',');
    return [...items].sort((a, b) => {
      let aValue = a[property];
      let bValue = b[property];
      // For description, compare as strings
      if (property === 'description') {
        aValue = aValue || '';
        bValue = bValue || '';
        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
      }
      // For id and grossAmount, compare as numbers
      if (property === 'id' || property === 'grossAmount') {
        aValue = Number(aValue);
        bValue = Number(bValue);
        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
      }
      return 0;
    });
  };

  // Use sorted items for rendering
  const sortedBillingItems = getSortedItems(billingItems);

  // Bulk selection
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(billingItems.map((item) => item.id));
    } else {
      setSelected([]);
    }
  };
  const handleSelect = (id: string | number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  // Bulk action
  const handleBulkReview = async () => {
    if (!user || !orderIdNum) return;
    setLoadingBulkAction(true);
    const userId = user.userProfile?.id || user.username;
    // Only include selected items with status 'CREATED' or 'CHANGES_REQUESTED'
    const reviewableIds = selected
      .filter((id) => {
        const item = billingItems.find((item) => item.id === id);
        return (
          item &&
          (item.status === 'CREATED' || item.status === 'CHANGES_REQUESTED')
        );
      })
      .map((id) => String(id));
    if (reviewableIds.length === 0) return;
    await reviewBillingItemsBulk(reviewableIds, userId, '');
    setSelected([]);
    // Refresh
    getBillingItemsByOrder(orderIdNum, page, rowsPerPage, sort).then(
      (res: PaginatedResponse<BillingItem>) => {
        setBillingItemsState(res.content);
        setTotalItems(res.totalElements);
      }
    );
    setLoadingBulkAction(false);
  };

  // Handler for bulk approve
  const handleApproveSelected = async () => {
    if (!user) return;
    setLoadingBulkAction(true);
    setBulkApproveLoading(true);
    const userId = user.userProfile?.id || user.username;

    // Filter only items that can be approved
    const approvableItems = selected.filter((id) => {
      const item = billingItems.find((item) => item.id === id);
      return item && canApproveOrReject(item);
    });

    if (approvableItems.length > 0) {
      const selectedIds = approvableItems.map((id) => String(id));
      await approveBillingItemsBulk(selectedIds, userId, '');
    }

    // Refresh
    if (orderIdNum) {
      getBillingItemsByOrder(orderIdNum, page, rowsPerPage, sort).then(
        (res: PaginatedResponse<BillingItem>) => {
          setBillingItemsState(res.content);
          setTotalItems(res.totalElements);
        }
      );
    }
    setBulkApproveLoading(false);
    setSelected([]);
    setLoadingBulkAction(false);
  };

  // Dialog action handler
  const handleAction = async () => {
    if (!actionDialog.itemId || !actionDialog.action || !user) return;
    const userId = user.userProfile?.id || user.username;
    if (!userId) return;
    if (actionDialog.action === 'approve') {
      await approveBillingItem(String(actionDialog.itemId), userId, comment);
    } else if (actionDialog.action === 'reject') {
      await rejectBillingItem(String(actionDialog.itemId), userId, comment);
    } else if (actionDialog.action === 'review') {
      await reviewBillingItem(String(actionDialog.itemId), userId, comment);
    }
    setActionDialog({ open: false, action: null, itemId: null });
    setComment('');
    // Refresh
    if (orderIdNum) {
      getBillingItemsByOrder(orderIdNum, page, rowsPerPage, sort).then(
        (res: PaginatedResponse<BillingItem>) => {
          setBillingItemsState(res.content);
          setTotalItems(res.totalElements);
        }
      );
    }
  };

  // 4-eye logic: cannot approve/reject if user is creator or reviewer
  const canApproveOrReject = (item: BillingItem) => {
    if (!user) return false;
    const userId = user.userProfile?.id || user.username;
    return (
      item.status === 'UNDER_REVIEW' &&
      item.createdBy !== userId &&
      item.verifiedBy !== userId &&
      item.approvedBy !== userId
    );
  };
  // Can review if item is CREATED and user is not the creator
  const canReview = (item: BillingItem) => {
    if (!user) return false;
    const userId = user.userProfile?.id;
    return item.status === 'CREATED' && item.createdBy !== userId;
  };

  console.log('BillingItemSummary currentStep:', currentStep);

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'hidden',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flex: 1
      }}
    >
      {loadingBulkAction && (
        <SuspenseLoader
          message={t(
            'billing.billing_item_summary.processing_selected',
            'Processing selected billing items...'
          )}
        />
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          'billing.billing_item_summary.info_banner',
          'Only approved billing items will appear in the Invoice Creation step. Please review and approve items as needed.'
        )}
      </Alert>
      <Typography variant="h6" mb={1}>
        {t('billing.billing_item_summary.title', 'Billing Item Approval')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t(
          'billing.billing_item_summary.description',
          'Review and approve billing items before proceeding to invoice creation. Select items and use bulk actions for efficiency.'
        )}
      </Typography>
      {(billingItemActionError || reviewBillingItemError) && (
        <Alert severity="error">
          {billingItemActionError || reviewBillingItemError}
        </Alert>
      )}
      <Paper
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <TableContainer
          sx={{ maxHeight: 'calc(100vh - 340px)', width: '100%', flex: 1 }}
        >
          <Table
            stickyHeader
            aria-label="Billing Item Summary Table"
            sx={{ tableLayout: 'fixed', width: '100%' }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '5%' }}>
                  <Checkbox
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < billingItems.length
                    }
                    checked={
                      billingItems.length > 0 &&
                      selected.length === billingItems.length
                    }
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': t('Select all billing items') }}
                  />
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <TableSortLabel
                    active={sort.startsWith('orderNumber')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('orderNumber')}
                  >
                    {t(
                      'billing.billing_item_summary.table.order_number',
                      'Order Number'
                    )}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <TableSortLabel
                    active={sort.startsWith('id')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('id')}
                  >
                    {t('billing.invoice_creation.table.billing_item_id')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '18%' }}>
                  <TableSortLabel
                    active={sort.startsWith('description')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('description')}
                  >
                    {t('billing.invoice_creation.table.description')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <TableSortLabel
                    active={sort.startsWith('grossAmount')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('grossAmount')}
                  >
                    {t('billing.invoice_creation.table.amount_gross')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '13%' }}>
                  <TableSortLabel
                    active={sort.startsWith('status')}
                    direction={sort.endsWith('desc') ? 'desc' : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    {t('billing.invoice_creation.table.status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '10%' }}>
                  {t('billingItems.table.creator', 'Creator')}
                </TableCell>
                {billingItems.some((item) => item.approvedBy) && (
                  <TableCell sx={{ width: '10%' }}>
                    {t('billingItems.table.approver', 'Approver')}
                  </TableCell>
                )}
                {billingItems.some((item) => item.comment) && (
                  <TableCell sx={{ width: '10%' }}>
                    {t('billingItems.table.comment', 'Comment')}
                  </TableCell>
                )}
                <TableCell sx={{ width: '15%' }}>
                  {t('billingItems.table.actions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : billingItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      gap={2}
                    >
                      <Typography variant="h6" color="text.secondary">
                        {t('billing.billing_item_summary.no_billing_items')}
                      </Typography>
                      <Typography color="text.secondary">
                        {t(
                          'billing.billing_item_summary.no_billing_items_subtitle'
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                sortedBillingItems.map((item, idx) => (
                  <TableRow
                    key={item.id}
                    hover
                    selected={selected.includes(item.id)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#f7fafd',
                      '&:hover': {
                        backgroundColor: '#eaf3fb'
                      }
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ py: 2 }}>
                      <Checkbox
                        checked={selected.includes(item.id)}
                        onChange={() => handleSelect(item.id)}
                        inputProps={{
                          'aria-label': t('Select billing item') + ' ' + item.id
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {order.requestReferenceId ||
                        order.orderNumber ||
                        order.id}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{item.id}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {item.description ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: formatBillingItemDescription(
                              item.description
                            )
                          }}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {item.grossAmount != null
                        ? `€${item.grossAmount.toFixed(2)}`
                        : '-'}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {/* Status badge with translation */}
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          fontWeight: 500,
                          color: 'white',
                          backgroundColor:
                            item.status === 'CREATED'
                              ? 'info.main'
                              : item.status === 'UNDER_REVIEW'
                              ? 'warning.main'
                              : item.status === 'APPROVED'
                              ? 'success.main'
                              : item.status === 'REJECTED'
                              ? 'error.main'
                              : 'grey.500',
                          fontSize: 13
                        }}
                      >
                        {t(
                          `billing.billing_item_summary.status.${item.status.toLowerCase()}`,
                          item.status
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {item.createdByName ? (
                        user &&
                        (item.createdBy === user.userProfile?.id ||
                          item.createdByName === user.userProfile?.name) ? (
                          <b>{item.createdByName}</b>
                        ) : (
                          item.createdByName
                        )
                      ) : item.createdBy ? (
                        user && item.createdBy === user.userProfile?.id ? (
                          <b>{item.createdBy}</b>
                        ) : (
                          item.createdBy
                        )
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    {billingItems.some((i) => i.approvedBy) && (
                      <TableCell sx={{ py: 2 }}>
                        {item.approvedByName ? (
                          user &&
                          (item.approvedBy === user.userProfile?.id ||
                            item.approvedByName === user.userProfile?.name) ? (
                            <b>{item.approvedByName}</b>
                          ) : (
                            item.approvedByName
                          )
                        ) : item.approvedBy ? (
                          user && item.approvedBy === user.userProfile?.id ? (
                            <b>{item.approvedBy}</b>
                          ) : (
                            item.approvedBy
                          )
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    )}
                    {billingItems.some((i) => i.comment) && (
                      <TableCell sx={{ py: 2 }}>
                        {item.comment || '-'}
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 2 }}>
                      {/* Tooltip for actions */}
                      {canReview(item) && (
                        <Button
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() =>
                            setActionDialog({
                              open: true,
                              action: 'review',
                              itemId: item.id
                            })
                          }
                          sx={{ mr: 1 }}
                          disabled={reviewBillingItemLoading}
                          aria-label={t(
                            'billing.billing_item_summary.dialog.review',
                            'Send to Review'
                          )}
                          title={t(
                            'billing.billing_item_summary.dialog.review_title',
                            'Send this item for review'
                          )}
                        >
                          {t(
                            'billing.billing_item_summary.dialog.review',
                            'Send to Review'
                          )}
                        </Button>
                      )}
                      {canApproveOrReject(item) && (
                        <>
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            onClick={() =>
                              setActionDialog({
                                open: true,
                                action: 'approve',
                                itemId: item.id
                              })
                            }
                            sx={{ mr: 1 }}
                            disabled={billingItemActionLoading}
                            aria-label={t(
                              'billing.billing_item_summary.dialog.approve',
                              'Approve'
                            )}
                            title={t(
                              'billing.billing_item_summary.dialog.approve_title',
                              'Approve this billing item'
                            )}
                          >
                            {t(
                              'billing.billing_item_summary.dialog.approve',
                              'Approve'
                            )}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() =>
                              setActionDialog({
                                open: true,
                                action: 'reject',
                                itemId: item.id
                              })
                            }
                            disabled={billingItemActionLoading}
                            aria-label={t(
                              'billing.billing_item_summary.dialog.reject',
                              'Reject'
                            )}
                            title={t(
                              'billing.billing_item_summary.dialog.reject_title',
                              'Reject this billing item'
                            )}
                          >
                            {t(
                              'billing.billing_item_summary.dialog.reject',
                              'Reject'
                            )}
                          </Button>
                        </>
                      )}
                      {item.status === 'CREATED' &&
                        user &&
                        (item.createdBy === user.userProfile?.id ||
                          item.createdBy === user.username) && (
                          <Button
                            variant="outlined"
                            color="info"
                            size="small"
                            onClick={() =>
                              setActionDialog({
                                open: true,
                                action: 'review',
                                itemId: item.id
                              })
                            }
                            sx={{ mr: 1 }}
                            disabled={reviewBillingItemLoading}
                            aria-label={t(
                              'billing.billing_item_summary.dialog.review',
                              'Send to Review'
                            )}
                            title={t(
                              'billing.billing_item_summary.dialog.review_title',
                              'Send this item for review'
                            )}
                          >
                            {t(
                              'billing.billing_item_summary.dialog.review',
                              'Send to Review'
                            )}
                          </Button>
                        )}
                    </TableCell>
                  </TableRow>
                ))
              )}
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <strong>
                    {t('billing.invoice_creation.table.total', 'Total')}
                  </strong>
                </TableCell>
                <TableCell colSpan={6} align="left">
                  <strong>{`€${
                    Array.isArray(billingItems)
                      ? billingItems
                          .reduce(
                            (sum, item) => sum + (item.grossAmount || 0),
                            0
                          )
                          .toFixed(2)
                      : '0.00'
                  }`}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          p={2}
        >
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="success"
              disabled={
                bulkApproveLoading ||
                selected.filter((id) => {
                  const item = billingItems.find((item) => item.id === id);
                  return item && canApproveOrReject(item);
                }).length === 0
              }
              onClick={handleApproveSelected}
            >
              {bulkApproveLoading
                ? t(
                    'billing.billing_item_summary.approving_selected',
                    'Approving...'
                  )
                : t(
                    'billing.billing_item_summary.approve_selected',
                    'Approve Selected'
                  )}
            </Button>
            <Button
              variant="contained"
              color="info"
              disabled={
                selected.filter((id) => {
                  const item = billingItems.find((item) => item.id === id);
                  return item && item.status === 'CREATED';
                }).length === 0
              }
              onClick={handleBulkReview}
              aria-label={t('Send Selected for Review')}
              title={t('Send all selected items for review')}
            >
              {t(
                'billing.billing_item_summary.send_selected_for_review',
                'Send Selected for Review'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            // Only pass selected APPROVED items, and convert id/orderId to string
            const approvedSelectedItems = billingItems
              .filter(
                (item) =>
                  selected.includes(item.id) && item.status === 'APPROVED'
              )
              .map((item) => ({
                ...item,
                id: String(item.id),
                orderId: String(item.orderId)
              }));
            setBillingItems(approvedSelectedItems);
            setCurrentStep('invoice-creation');
          }}
          disabled={
            billingItems.filter(
              (item) => selected.includes(item.id) && item.status === 'APPROVED'
            ).length === 0
          }
          title={
            billingItems.filter(
              (item) => selected.includes(item.id) && item.status === 'APPROVED'
            ).length === 0
              ? t(
                  'billing.billing_item_summary.proceed_tooltip',
                  'You must select at least one approved billing item to proceed.'
                )
              : undefined
          }
        >
          {t(
            'billing.billing_item_summary.proceed_to_invoice_creation',
            'Proceed to Invoice Creation'
          )}
        </Button>
        {billingItems.filter(
          (item) => selected.includes(item.id) && item.status === 'APPROVED'
        ).length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 2, alignSelf: 'center' }}
          >
            {t(
              'billing.billing_item_summary.proceed_inline',
              'You must select at least one approved billing item to proceed.'
            )}
          </Typography>
        )}
      </Box>
      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() =>
          setActionDialog({ open: false, action: null, itemId: null })
        }
        maxWidth="sm"
        fullWidth
        aria-labelledby="billing-item-action-dialog-title"
      >
        <DialogTitle id="billing-item-action-dialog-title">
          {actionDialog.action === 'approve'
            ? t(
                'billing.billing_item_summary.dialog.approve_title',
                'Approve Billing Item'
              )
            : actionDialog.action === 'reject'
            ? t(
                'billing.billing_item_summary.dialog.reject_title',
                'Reject Billing Item'
              )
            : t(
                'billing.billing_item_summary.dialog.review_title',
                'Send Billing Item to Review'
              )}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t(
              'billing.billing_item_summary.dialog.comment_label',
              'Comment'
            )}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 2 }}
            placeholder={t(
              'billing.billing_item_summary.dialog.comment_placeholder',
              'Add your comments here...'
            )}
            inputProps={{ 'aria-label': t('Comment') }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActionDialog({ open: false, action: null, itemId: null })
            }
            color="inherit"
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleAction}
            variant="contained"
            color={
              actionDialog.action === 'approve'
                ? 'success'
                : actionDialog.action === 'reject'
                ? 'error'
                : 'info'
            }
            disabled={billingItemActionLoading || reviewBillingItemLoading}
            aria-label={t(
              'billing.billing_item_summary.dialog.confirm_action',
              'Confirm action'
            )}
          >
            {actionDialog.action === 'approve'
              ? t('billing.billing_item_summary.dialog.approve', 'Approve')
              : actionDialog.action === 'reject'
              ? t('billing.billing_item_summary.dialog.reject', 'Reject')
              : t(
                  'billing.billing_item_summary.dialog.review',
                  'Send to Review'
                )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingItemSummary;
